// ============================================================
// Pay Rocky - Home Page
// ============================================================

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Zap, Clock } from 'lucide-react';
import { PublicLayout, PageContainer } from '../components/layout/Layout';
import { Button, Card, LoadingCard, Skeleton } from '../components/ui/Primitives';
import TelegramCommunityCard from '../components/ui/TelegramCommunityCard';
import { useRates } from '../context/RatesContext';
import { API_BASE_URL, CRYPTO_LIST, DEFAULT_HOMEPAGE_BANNER, HOMEPAGE_RATE_COIN_IDS } from '../data/constants';
import CryptoLogo from '../components/CryptoLogo';
import { formatCrypto, formatNGN } from '../utils/helpers';
import { apiRequest, ensureBackendReady } from '../utils/api';
import { readCachedValue, writeCachedValue } from '../utils/storage';

const HERO_BANNERS = [
  {
    id: 'fast-settlement',
    eyebrow: 'Fast Settlements',
    title: 'Move from Naira payment to crypto delivery without delay',
    body: 'Place an order, lock your rate, upload payment proof, and let the PayRocky team process your transaction in one clean flow.',
    supportLabel: 'Processing flow',
    supportText: 'Built for users who want a simple path from order placement to wallet delivery without unnecessary waiting.',
    visualAccent: 'linear-gradient(135deg, #f6ede6 0%, #efc8b5 45%, #c77258 100%)',
    visualPattern: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.7) 0, rgba(255,255,255,0) 30%), radial-gradient(circle at 78% 32%, rgba(255,255,255,0.45) 0, rgba(255,255,255,0) 26%), linear-gradient(135deg, rgba(44,24,19,0.08), rgba(44,24,19,0))',
    visualStat: '15 min',
    visualCaption: 'Rate lock',
    cta: 'Start Buying',
    action: '/buy',
  },
  {
    id: 'secure-verification',
    eyebrow: 'Verified Trading',
    title: 'Trade on a platform built around KYC and payment-name checks',
    body: 'Verified profiles, matched payer names, and PayRocky review steps help reduce risky transfers and keep each order accountable.',
    supportLabel: 'Compliance layer',
    supportText: 'Users buying crypto are guided through identity verification and payer-name rules before an order is approved.',
    visualAccent: 'linear-gradient(135deg, #fbf6ef 0%, #e9d8c8 48%, #8f3b2e 100%)',
    visualPattern: 'radial-gradient(circle at 24% 24%, rgba(255,255,255,0.8) 0, rgba(255,255,255,0) 28%), linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(44,24,19,0.08) 100%)',
    visualStat: '100%',
    visualCaption: 'Profile match',
    cta: 'Verify Profile',
    action: '/dashboard',
  },
  {
    id: 'network-flexibility',
    eyebrow: 'Multi-Network Support',
    title: 'Choose the right blockchain network before you confirm your order',
    body: 'BTC, ETH, USDT, and USDC network options are presented clearly so users can see the fee, payout impact, and wallet route before proceeding.',
    supportLabel: 'Network control',
    supportText: 'Different coins and networks can carry different fees, and PayRocky shows that clearly before the customer commits.',
    visualAccent: 'linear-gradient(135deg, #eef3f6 0%, #d7e6ee 42%, #7a4b35 100%)',
    visualPattern: 'radial-gradient(circle at 70% 24%, rgba(255,255,255,0.6) 0, rgba(255,255,255,0) 24%), linear-gradient(135deg, rgba(255,255,255,0.22), rgba(44,24,19,0.12))',
    visualStat: '4x',
    visualCaption: 'Stablecoin routes',
    cta: 'View Rates',
    action: '/rates',
  },
  {
    id: 'community-growth',
    eyebrow: 'Community Growth',
    title: 'Join a growing crypto community built around updates and education',
    body: 'Stay close to PayRocky through Telegram for market updates, trading education, announcements, and a more interactive user community.',
    supportLabel: 'Community access',
    supportText: 'The Telegram channel keeps customers connected to education, platform news, and a more active trading audience.',
    visualAccent: 'linear-gradient(135deg, #f4efe8 0%, #eed8cb 38%, #b6523f 100%)',
    visualPattern: 'radial-gradient(circle at 28% 70%, rgba(255,255,255,0.5) 0, rgba(255,255,255,0) 26%), linear-gradient(160deg, rgba(255,255,255,0.18), rgba(44,24,19,0.12))',
    visualStat: '24/7',
    visualCaption: 'Community updates',
    cta: 'Sell Crypto',
    action: '/sell',
  },
];
const PUBLIC_ORDERS_CACHE_KEY = 'payrocky_public_orders';
const PUBLIC_ORDERS_CACHE_TTL_MS = 5 * 60 * 1000;
const HOMEPAGE_BANNER_CACHE_KEY = 'payrocky_homepage_whatsapp_banner';
const HOMEPAGE_BANNER_CACHE_TTL_MS = 10 * 1000;
function normalizeHomepageBanner(source = {}) {
  return {
    image_url: source.image_url || '',
    whatsapp_number: String(source.whatsapp_number || '').trim(),
    whatsapp_message: source.whatsapp_message || '',
    updated_at: source.updated_at || '',
  };
}

function buildWhatsappLink(number, message) {
  const sanitizedNumber = String(number || '').replace(/[^\d]/g, '');
  if (!sanitizedNumber) return '';
  const encodedMessage = encodeURIComponent(message || 'Hello PayRocky, I would like to know more about this offer.');
  return `https://wa.me/${sanitizedNumber}?text=${encodedMessage}`;
}

function resolveBannerImageUrl(fileUrl) {
  if (!fileUrl) return '';
  if (/^https?:\/\//i.test(fileUrl)) return encodeURI(fileUrl);
  const uploadsBaseUrl = API_BASE_URL.replace(/\/api\/?$/, '');
  const normalizedPath = fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`;
  return `${uploadsBaseUrl}${encodeURI(normalizedPath)}`;
}

export default function HomePage() {
  const navigate = useNavigate();
  const { rates } = useRates();
  const [recentOrders, setRecentOrders] = useState(() => readCachedValue(PUBLIC_ORDERS_CACHE_KEY, PUBLIC_ORDERS_CACHE_TTL_MS, []));
  const [homepageBanner, setHomepageBanner] = useState(() => normalizeHomepageBanner(readCachedValue(HOMEPAGE_BANNER_CACHE_KEY, HOMEPAGE_BANNER_CACHE_TTL_MS, DEFAULT_HOMEPAGE_BANNER)));
  const [activeBanner, setActiveBanner] = useState(0);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [bannerLoading, setBannerLoading] = useState(false);
  const [bannerImageBroken, setBannerImageBroken] = useState(false);

  useEffect(() => {
    ensureBackendReady(10000).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function fetchRecentOrders() {
      const cachedOrders = readCachedValue(PUBLIC_ORDERS_CACHE_KEY, PUBLIC_ORDERS_CACHE_TTL_MS, null);
      if (cachedOrders && !cancelled) {
        setRecentOrders(cachedOrders);
      }

      if (!cachedOrders && !cancelled) {
        setOrdersLoading(true);
      }

      try {
        const res = await apiRequest('/public/orders');
        if (!cancelled) {
          setRecentOrders((res.orders || []).slice(0, 4));
        }
        writeCachedValue(PUBLIC_ORDERS_CACHE_KEY, (res.orders || []).slice(0, 4));
      } catch (_) {
        if (!cancelled && !cachedOrders) setRecentOrders([]);
      } finally {
        if (!cancelled) {
          setOrdersLoading(false);
        }
      }
    }

    fetchRecentOrders();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function fetchHomepageBanner() {
      const cachedBanner = readCachedValue(HOMEPAGE_BANNER_CACHE_KEY, HOMEPAGE_BANNER_CACHE_TTL_MS, null);
      if (cachedBanner && !cancelled) {
        setHomepageBanner(normalizeHomepageBanner(cachedBanner));
      }

      if (!cancelled) {
        setBannerLoading(true);
      }

      try {
        const res = await apiRequest('/public/homepage-banner');
        const next = normalizeHomepageBanner(res.banner);
        if (!cancelled && (next.image_url || next.whatsapp_number || next.whatsapp_message)) {
          setHomepageBanner(next);
        }
        if (next.image_url || next.whatsapp_number || next.whatsapp_message) {
          writeCachedValue(HOMEPAGE_BANNER_CACHE_KEY, next);
        }
      } catch (_) {
        if (!cancelled && !cachedBanner && !homepageBanner.image_url) {
          setHomepageBanner(normalizeHomepageBanner(DEFAULT_HOMEPAGE_BANNER));
        }
      } finally {
        if (!cancelled) {
          setBannerLoading(false);
        }
      }
    }

    fetchHomepageBanner();
    return () => {
      cancelled = true;
    };
  }, [homepageBanner.image_url]);
  const transactionSlides = useMemo(
    () =>
      recentOrders
        .filter((order) => order.status !== 'CANCELLED')
        .slice(0, 4)
        .map((order) => {
        const asset = CRYPTO_LIST.find((crypto) => crypto.id === order.crypto_type);
        return {
          id: order.id,
          orderId: order.order_id,
          type: order.type,
          status: order.status,
          assetName: asset?.name || order.crypto_type.toUpperCase(),
          assetSymbol: asset?.symbol || order.crypto_type.toUpperCase(),
          assetId: asset?.id || order.crypto_type,
          assetColor: asset?.color || 'var(--clr-accent)',
          network: order.network,
          cryptoAmount: formatCrypto(order.crypto_amount, asset?.symbol),
          nairaAmount: formatNGN(order.naira_value),
        };
      }),
    [recentOrders]
  );
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    if (transactionSlides.length <= 1) return undefined;
    const intervalId = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % transactionSlides.length);
    }, 3200);

    return () => window.clearInterval(intervalId);
  }, [transactionSlides.length]);

  useEffect(() => {
    if (activeSlide >= transactionSlides.length) {
      setActiveSlide(0);
    }
  }, [activeSlide, transactionSlides.length]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setActiveBanner((current) => (current + 1) % HERO_BANNERS.length);
    }, 4200);

    return () => window.clearInterval(intervalId);
  }, []);

  const currentSlide = transactionSlides[activeSlide];
  const currentBanner = HERO_BANNERS[activeBanner];
  const homepageBannerLink = useMemo(
    () => buildWhatsappLink(homepageBanner.whatsapp_number, homepageBanner.whatsapp_message),
    [homepageBanner.whatsapp_message, homepageBanner.whatsapp_number]
  );
  const homepageBannerImageUrl = useMemo(
    () => resolveBannerImageUrl(homepageBanner.image_url),
    [homepageBanner.image_url]
  );
  const homepageBannerVersionedImageUrl = useMemo(() => {
    if (!homepageBannerImageUrl) return '';
    const versionToken = encodeURIComponent(homepageBanner.updated_at || homepageBanner.image_url);
    return `${homepageBannerImageUrl}${homepageBannerImageUrl.includes('?') ? '&' : '?'}v=${versionToken}`;
  }, [homepageBanner.image_url, homepageBanner.updated_at, homepageBannerImageUrl]);

  useEffect(() => {
    setBannerImageBroken(false);
  }, [homepageBannerVersionedImageUrl]);

  return (
    <PublicLayout>
      <section
        className="home-hero-section"
        style={{
          padding: 'clamp(3rem, 8vw, 6rem) 1.5rem',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: '8% auto auto 50%',
            transform: 'translateX(-50%)',
            width: '760px',
            height: '420px',
            background: 'radial-gradient(ellipse, rgba(182, 82, 63, 0.14) 0%, rgba(143, 59, 46, 0.05) 42%, transparent 72%)',
            pointerEvents: 'none',
          }}
        />

        <div className="home-hero-inner" style={{ maxWidth: '820px', margin: '0 auto', position: 'relative' }}>
          <div
            className="fade-up home-hero-kicker"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 14px',
              borderRadius: '99px',
              background: 'rgba(255, 255, 255, 0.78)',
              border: '1px solid var(--clr-accent-glow)',
              color: 'var(--clr-accent)',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              marginBottom: '1.5rem',
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            <span className="pulse-dot" />
            Live Trading - Nigeria
          </div>

          <h1
            className="fade-up fade-up-1"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(2.4rem, 6vw, 4.4rem)',
              fontWeight: 800,
              lineHeight: 1.05,
              letterSpacing: '-0.04em',
              marginBottom: '1.25rem',
              color: 'var(--clr-text)',
            }}
          >
            Buy and sell crypto
            <br />
            with <span style={{ color: 'var(--clr-accent)' }}>trusted Naira transfers</span>
          </h1>

          <p
            className="fade-up fade-up-2 home-hero-copy"
            style={{
              fontSize: 'clamp(14px, 2vw, 16px)',
              color: 'var(--clr-text-2)',
              maxWidth: '580px',
              margin: '0 auto 2rem',
              lineHeight: 1.8,
            }}
          >
                A lighter, clearer PayRocky experience for fast Crypto Trading. Move from quote to transfer to delivery without the clutter of a dark trading screen.
          </p>

          <div className="fade-up fade-up-3 home-hero-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button className="home-hero-button" variant="primary" size="lg" onClick={() => navigate('/buy')}>
              Buy Crypto <ArrowRight size={16} />
            </Button>
            <Button className="home-hero-button" variant="ghost" size="lg" onClick={() => navigate('/sell')}>
              Sell Crypto
            </Button>
          </div>
        </div>
      </section>

      <PageContainer style={{ paddingTop: 0 }}>
        <section className="home-feature-section" style={{ marginBottom: '2.5rem' }}>
          <div className="home-section-heading" style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--clr-text-2)', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '8px' }}>
              Instants Crypto transfer
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.5rem, 3.5vw, 2.15rem)', fontWeight: 800, color: 'var(--clr-text)' }}>
              Featured Highlights
            </div>
          </div>

          <Card
            className="home-banner-card"
            style={{
              padding: '0',
              overflow: 'hidden',
              background: 'linear-gradient(135deg, rgba(255,255,255,0.97) 0%, rgba(244, 235, 226, 0.98) 100%)',
            }}
          >
            <div className="home-banner-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.1fr) minmax(320px, 0.9fr)', alignItems: 'stretch' }}>
              <div className="home-banner-copy" style={{ padding: '2rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 0 }}>
                <div>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '7px 12px', borderRadius: '999px', background: 'var(--clr-accent-dim)', color: 'var(--clr-accent)', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1rem' }}>
                    <span className="pulse-dot" style={{ width: '6px', height: '6px' }} />
                    {currentBanner.eyebrow}
                  </div>
                  <div className="home-banner-title" style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', fontWeight: 800, lineHeight: 1.08, color: 'var(--clr-text)', marginBottom: '0.85rem' }}>
                    {currentBanner.title}
                  </div>
                  <div className="home-banner-body" style={{ fontSize: '14px', color: 'var(--clr-text-2)', lineHeight: 1.8, maxWidth: '560px' }}>
                    {currentBanner.body}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', marginTop: '1.5rem', flexWrap: 'wrap' }}>
                  <Button variant="primary" onClick={() => navigate(currentBanner.action)}>
                    {currentBanner.cta} <ArrowRight size={15} />
                  </Button>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    {HERO_BANNERS.map((banner, index) => (
                      <button
                        key={banner.id}
                        onClick={() => setActiveBanner(index)}
                        aria-label={`Show banner ${index + 1}`}
                        style={{
                          width: index === activeBanner ? '30px' : '9px',
                          height: '9px',
                          borderRadius: '999px',
                          background: index === activeBanner ? 'var(--clr-accent)' : 'var(--clr-surface-3)',
                          transition: 'all 0.2s ease',
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div
                className="home-banner-visual"
                style={{
                  position: 'relative',
                  minHeight: '340px',
                  overflow: 'hidden',
                  background: currentBanner.visualAccent,
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: currentBanner.visualPattern,
                  }}
                />
                <div
                  className="home-banner-visual-badge"
                  style={{
                    position: 'absolute',
                    top: '26px',
                    left: '24px',
                    width: '92px',
                    height: '92px',
                    borderRadius: '24px',
                    background: 'rgba(255,255,255,0.18)',
                    border: '1px solid rgba(255,255,255,0.35)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontFamily: 'var(--font-display)',
                    fontWeight: 800,
                    fontSize: '2rem',
                    letterSpacing: '-0.04em',
                  }}
                >
                  {currentBanner.eyebrow.charAt(0)}
                </div>
                <div
                  className="home-banner-visual-stat"
                  style={{
                    position: 'absolute',
                    right: '24px',
                    top: '28px',
                    padding: '12px 14px',
                    borderRadius: '18px',
                    background: 'rgba(44,24,19,0.18)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    color: '#fff',
                    minWidth: '120px',
                    textAlign: 'right',
                  }}
                >
                  <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.84 }}>
                    {currentBanner.visualCaption}
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 800, lineHeight: 1.1 }}>
                    {currentBanner.visualStat}
                  </div>
                </div>
                <div className="home-banner-floating-card" style={{ position: 'absolute', left: '20px', right: '20px', bottom: '20px', padding: '14px 16px', borderRadius: '18px', background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.7)', boxShadow: '0 18px 40px rgba(105, 61, 43, 0.14)' }}>
                  <div className="home-banner-floating-card-label" style={{ fontSize: '11px', color: 'var(--clr-text-3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '5px' }}>
                    {currentBanner.supportLabel}
                  </div>
                  <div className="home-banner-floating-card-title" style={{ fontSize: '15px', fontWeight: 800, color: 'var(--clr-text)', marginBottom: '4px' }}>
                    {currentBanner.eyebrow}
                  </div>
                  <div className="home-banner-floating-card-copy" style={{ fontSize: '12px', color: 'var(--clr-text-2)', lineHeight: 1.6 }}>
                    {currentBanner.supportText}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </section>

        {bannerLoading && !homepageBannerImageUrl ? (
          <section className="home-alert-board-section" style={{ marginBottom: '1.75rem' }}>
            <Card style={{ padding: '0.9rem', overflow: 'hidden' }}>
              <Skeleton width="100%" height="190px" radius="20px" />
            </Card>
          </section>
        ) : homepageBannerImageUrl && homepageBannerLink && !bannerImageBroken ? (
          <section style={{ marginBottom: '1.75rem' }}>
            <a
              href={homepageBannerLink}
              target="_blank"
              rel="noreferrer"
              className="homepage-whatsapp-banner"
              style={{
                display: 'block',
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '24px',
                border: '1px solid rgba(172, 118, 100, 0.18)',
                boxShadow: '0 22px 50px rgba(105, 61, 43, 0.12)',
                background: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(244, 235, 226, 0.98))',
                padding: '10px',
              }}
            >
              <div className="homepage-whatsapp-banner-frame" style={{ position: 'relative', minHeight: '170px', aspectRatio: '16 / 5', background: 'var(--clr-surface-2)', borderRadius: '18px', overflow: 'hidden' }}>
                <img
                  src={homepageBannerVersionedImageUrl}
                  alt="PayRocky Alert Board"
                  loading="lazy"
                  onError={() => setBannerImageBroken(true)}
                  style={{
                    width: '100%',
                    height: '100%',
                    minHeight: '170px',
                    maxHeight: '230px',
                    objectFit: 'contain',
                    objectPosition: 'center',
                    display: 'block',
                    background: 'var(--clr-surface-2)',
                    padding: '0',
                  }}
                />
              </div>
            </a>
          </section>
        ) : null}

        <section className="home-transactions-section" style={{ marginBottom: '3.5rem' }}>
          <div className="home-section-heading" style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--clr-text-2)', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '8px' }}>
              Transaction PublicBoard
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.7rem, 4vw, 2.4rem)', fontWeight: 800, color: 'var(--clr-text)', marginBottom: '8px' }}>
              Recent transactions Live on PayRocky
            </div>
            <div style={{ color: 'var(--clr-text-2)', fontSize: '14px', maxWidth: '620px', margin: '0 auto' }}>
                This rotating showcase helps visitors see the kind of buy and sell activity happening on PayRocky before they place their own order.
            </div>
          </div>

          {ordersLoading && !currentSlide ? (
            <LoadingCard lines={4} titleWidth="30%" />
          ) : currentSlide ? (
            <Card
              className="home-transaction-shell"
              style={{
                padding: '1.05rem',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(244, 235, 226, 0.98) 100%)',
              }}
            >
              <div
                className="home-transaction-grid"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1.2fr) minmax(280px, 0.8fr)',
                  gap: '0',
                  alignItems: 'stretch',
                  minWidth: 0,
                }}
              >
                <div
                  className="home-slide-primary"
                  style={{
                    padding: '1.6rem',
                    borderRadius: '20px 0 0 20px',
                    background: 'transparent',
                    border: '0',
                    minWidth: 0,
                  }}
                >
                  <div className="home-slide-primary-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
                    <div className="home-slide-primary-summary" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div
                        className="home-slide-primary-icon"
                        style={{
                          width: '52px',
                          height: '52px',
                          borderRadius: '16px',
                          background: `${currentSlide.assetColor}18`,
                          color: currentSlide.assetColor,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '24px',
                          fontWeight: 700,
                        }}
                      >
                        <CryptoLogo cryptoId={currentSlide.assetId} size={36} />
                      </div>
                      <div className="home-slide-primary-copy" style={{ minWidth: 0 }}>
                        <div className="home-slide-primary-title" style={{ fontFamily: 'var(--font-display)', fontSize: '1.25rem', fontWeight: 800, color: 'var(--clr-text)' }}>
                          {currentSlide.type === 'buy' ? 'Buy completed' : 'Sell in progress'}
                        </div>
                        <div className="home-slide-primary-subtitle" style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>
                          {currentSlide.assetName} on {currentSlide.network}
                        </div>
                      </div>
                    </div>

                    <div
                      className="home-slide-status-pill"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 12px',
                        borderRadius: '999px',
                        background: 'rgba(255, 255, 255, 0.75)',
                        border: '1px solid var(--clr-border)',
                        color: 'var(--clr-accent)',
                        fontSize: '12px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                      }}
                    >
                      <span className="pulse-dot" style={{ width: '6px', height: '6px' }} />
                      {currentSlide.status}
                    </div>
                  </div>

                  <div className="home-slide-metrics-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px' }}>
                    <SlideMetric label="Transaction Type" value={currentSlide.type === 'buy' ? 'Buy order' : 'Sell order'} />
                    <SlideMetric label="Order ID" value={currentSlide.orderId} mono />
                    <SlideMetric label="Crypto Amount" value={`${currentSlide.cryptoAmount} ${currentSlide.assetSymbol}`} />
                    <SlideMetric label="Naira Value" value={currentSlide.nairaAmount} accent />
                  </div>
                </div>

                <div className="home-slide-queue" style={{ padding: '1.35rem', borderRadius: '0 20px 20px 0', background: 'rgba(143, 59, 46, 0.04)', borderLeft: '1px solid rgba(172, 118, 100, 0.18)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 0 }}>
                  <div>
                    <div className="home-slide-queue-heading" style={{ fontSize: '12px', fontWeight: 700, color: 'var(--clr-text-2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1rem' }}>
                      Slideshow Queue
                    </div>
                    <div style={{ display: 'grid', gap: '10px' }}>
                      {transactionSlides.map((slide, index) => (
                        <button
                          key={slide.id}
                          onClick={() => setActiveSlide(index)}
                          className="home-slide-queue-item"
                          style={{
                            width: '100%',
                            textAlign: 'left',
                            padding: '12px 14px',
                            borderRadius: '14px',
                            border: `1px solid ${index === activeSlide ? 'var(--clr-accent-glow)' : 'var(--clr-border)'}`,
                            background: index === activeSlide ? 'var(--clr-accent-dim)' : 'var(--clr-surface)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '12px',
                          }}
                        >
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--clr-text)' }}>
                              {slide.type === 'buy' ? 'Buy' : 'Sell'} {slide.assetSymbol}
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--clr-text-3)' }}>{slide.orderId}</div>
                          </div>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: index === activeSlide ? 'var(--clr-accent)' : 'var(--clr-text-2)' }}>
                            {slide.nairaAmount}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '1.25rem' }}>
                    {transactionSlides.map((slide, index) => (
                      <button
                        key={slide.id}
                        onClick={() => setActiveSlide(index)}
                        aria-label={`Show transaction ${index + 1}`}
                        style={{
                          width: index === activeSlide ? '28px' : '8px',
                          height: '8px',
                          borderRadius: '999px',
                          background: index === activeSlide ? 'var(--clr-accent)' : 'var(--clr-surface-3)',
                          transition: 'all 0.2s ease',
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          ) : (
            <Card
              style={{
                padding: '1.6rem',
                textAlign: 'center',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.94) 0%, rgba(244, 235, 226, 0.98) 100%)',
              }}
            >
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 800, color: 'var(--clr-text)', marginBottom: '8px' }}>
                Recent transactions will appear here
              </div>
              <div style={{ color: 'var(--clr-text-2)', fontSize: '14px', maxWidth: '560px', margin: '0 auto' }}>
                Once live buy and sell orders start coming in, this section will automatically rotate through the latest completed and active transactions.
              </div>
            </Card>
          )}
        </section>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '1rem',
            flexWrap: 'wrap',
            gap: '8px',
          }}
        >
          <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--clr-text-2)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            Live Rates
          </div>
          <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span className="pulse-dot" style={{ width: '5px', height: '5px' }} />
            Updates every 60s
          </div>
        </div>

        <div className="home-rates-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px', marginBottom: '3.5rem' }}>
          {CRYPTO_LIST.filter((crypto) => HOMEPAGE_RATE_COIN_IDS.includes(crypto.id)).map((crypto) => {
            const rate = rates[crypto.id];
            return (
              <Card key={crypto.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <CryptoLogo cryptoId={crypto.id} size={40} />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--clr-text)' }}>{crypto.name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--clr-text-3)' }}>{crypto.symbol}</div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '13px', color: 'var(--clr-accent)', fontWeight: 700 }}>
                    {rate ? formatNGN(rate.buy) : '-'}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--clr-text-3)' }}>Buy rate</div>
                  <div style={{ fontSize: '12px', color: 'var(--clr-danger)', fontWeight: 600, marginTop: '2px' }}>
                    {rate ? formatNGN(rate.sell) : '-'}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--clr-text-3)' }}>Sell rate</div>
                </div>
              </Card>
            );
          })}
        </div>

        <section style={{ marginBottom: '3.5rem' }}>
          <TelegramCommunityCard />
        </section>

        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', fontWeight: 800, marginBottom: '6px', color: 'var(--clr-text)' }}>
            How it works
          </div>
          <div style={{ color: 'var(--clr-text-2)', fontSize: '14px' }}>Three simple steps</div>
        </div>

        <div className="home-steps-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px', marginBottom: '4rem' }}>
          {[
            { step: '01', icon: <Zap size={20} />, title: 'Place an order', body: 'Select your crypto, enter your amount, and lock in the quote for 15 minutes.' },
            { step: '02', icon: <Clock size={20} />, title: 'Make payment', body: 'Send your Naira transfer and upload payment proof from the same clean order flow.' },
            { step: '03', icon: <ShieldCheck size={20} />, title: 'Receive crypto', body: 'Our team verifies the transfer and sends your assets directly to your wallet.' },
          ].map(({ step, icon, title, body }) => (
            <Card key={step}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    background: 'var(--clr-accent-dim)',
                    color: 'var(--clr-accent)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {icon}
                </div>
                <div style={{ fontSize: '11px', fontFamily: 'var(--font-mono)', color: 'var(--clr-text-3)' }}>{step}</div>
              </div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '15px', fontWeight: 700, marginBottom: '6px', color: 'var(--clr-text)' }}>{title}</div>
              <div style={{ fontSize: '13px', color: 'var(--clr-text-2)', lineHeight: 1.6 }}>{body}</div>
            </Card>
          ))}
        </div>

        <div
          className="home-trust-grid"
          style={{
            display: 'flex',
            gap: '1.5rem',
            justifyContent: 'center',
            flexWrap: 'wrap',
            padding: '2rem',
            background: 'rgba(255, 255, 255, 0.82)',
            borderRadius: 'var(--radius-xl)',
            border: '1px solid var(--clr-border)',
            boxShadow: '0 20px 45px rgba(105, 61, 43, 0.07)',
          }}
        >
          {[
            { label: 'Manual Verification', sub: 'Every transaction reviewed' },
            { label: '15-Min Rate Lock', sub: 'Price guaranteed on order' },
            { label: 'Nigerian Banks', sub: 'All major banks supported' },
            { label: 'BTC - ETH - USDT - USDC', sub: 'More assets supported' },
          ].map(({ label, sub }) => (
            <div key={label} style={{ textAlign: 'center', minWidth: '150px' }}>
              <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '2px', color: 'var(--clr-text)' }}>{label}</div>
              <div style={{ fontSize: '12px', color: 'var(--clr-text-3)' }}>{sub}</div>
            </div>
          ))}
        </div>

        <style>{`
          @media (max-width: 1100px) {
            .home-banner-grid {
              grid-template-columns: 1fr !important;
            }

            .home-banner-visual {
              min-height: 300px !important;
            }

            .home-transaction-grid {
              grid-template-columns: 1fr;
              gap: 0 !important;
            }

            .home-slide-primary {
              border-radius: 20px 20px 0 0 !important;
            }

            .home-slide-queue {
              border-radius: 0 0 20px 20px !important;
            }
          }

          @media (max-width: 900px) {
            .home-transaction-grid {
              grid-template-columns: 1fr;
            }

            .home-slide-queue {
              border-left: 0 !important;
              border-top: 1px solid rgba(172, 118, 100, 0.18) !important;
            }
          }

          @media (max-width: 768px) {
            .home-banner-copy {
              padding: 1.25rem !important;
            }

            .home-banner-visual {
              min-height: 250px !important;
            }

            .home-banner-visual-badge {
              top: 18px !important;
              left: 16px !important;
              width: 72px !important;
              height: 72px !important;
              border-radius: 18px !important;
              font-size: 1.45rem !important;
            }

            .home-banner-visual-stat {
              top: 18px !important;
              right: 16px !important;
              min-width: 98px !important;
              padding: 10px 12px !important;
              border-radius: 14px !important;
            }

            .home-rates-grid {
              grid-template-columns: 1fr !important;
            }

            .home-steps-grid {
              grid-template-columns: 1fr !important;
            }

            .home-trust-grid {
              padding: 1.4rem !important;
              gap: 1rem !important;
            }

            .homepage-whatsapp-banner {
              border-radius: 20px !important;
              padding: 8px !important;
            }
          }

          @media (max-width: 640px) {
            .home-hero-button {
              width: 100%;
            }

            .home-banner-card {
              border-radius: 18px !important;
            }

            .home-banner-copy {
              padding: 1rem !important;
            }

            .home-banner-title {
              font-size: 1.45rem !important;
            }

            .home-banner-body {
              font-size: 13px !important;
              line-height: 1.7 !important;
            }

            .home-banner-visual {
              min-height: 220px !important;
            }

            .home-banner-floating-card {
              left: 12px !important;
              right: 12px !important;
              bottom: 12px !important;
              padding: 12px !important;
            }

            .home-banner-visual-badge {
              top: 14px !important;
              left: 12px !important;
              width: 58px !important;
              height: 58px !important;
              border-radius: 16px !important;
              font-size: 1.15rem !important;
            }

            .home-banner-visual-stat {
              top: 14px !important;
              right: 12px !important;
              min-width: 84px !important;
              padding: 8px 10px !important;
              border-radius: 12px !important;
            }

            .home-banner-visual-stat div:first-child {
              font-size: 9px !important;
            }

            .home-banner-visual-stat div:last-child {
              font-size: 1.1rem !important;
            }

            .home-banner-floating-card-label {
              font-size: 10px !important;
            }

            .home-banner-floating-card-title {
              font-size: 13px !important;
              margin-bottom: 2px !important;
            }

            .home-banner-floating-card-copy {
              font-size: 11px !important;
              line-height: 1.5 !important;
            }

            .homepage-whatsapp-banner img {
              min-height: 150px !important;
              max-height: 185px !important;
            }

            .homepage-whatsapp-banner-frame {
              min-height: 150px !important;
              aspect-ratio: 16 / 6 !important;
              border-radius: 16px !important;
            }

              .home-slide-primary {
                padding: 1rem !important;
              }

            .home-slide-queue {
              padding: 0.95rem !important;
            }

            .home-transaction-shell {
              padding: 0.85rem !important;
            }

            .home-transaction-grid {
              padding: 0 !important;
              border-radius: 18px !important;
            }

            .home-slide-queue-item {
              padding: 9px 11px !important;
            }

            .home-slide-metrics-grid {
              grid-template-columns: 1fr !important;
              gap: 10px !important;
            }

            .home-slide-primary-header {
              gap: 10px !important;
              margin-bottom: 1rem !important;
            }

            .home-slide-primary-summary {
              width: 100% !important;
              align-items: flex-start !important;
            }

            .home-slide-primary-icon {
              width: 42px !important;
              height: 42px !important;
              border-radius: 12px !important;
              font-size: 18px !important;
              flex-shrink: 0 !important;
            }

            .home-slide-primary-title {
              font-size: 1.05rem !important;
              line-height: 1.15 !important;
            }

            .home-slide-primary-subtitle {
              font-size: 12px !important;
              line-height: 1.5 !important;
            }

            .home-slide-status-pill {
              width: 100% !important;
              justify-content: center !important;
              padding: 7px 10px !important;
              font-size: 11px !important;
            }

            .home-slide-queue {
              margin-top: 0 !important;
            }
          }

          @media (max-width: 560px) {
            .home-banner-visual {
              min-height: 205px !important;
            }

            .home-transaction-grid {
              gap: 0 !important;
            }

            .home-slide-primary {
              padding-bottom: 0.6rem !important;
            }

            .home-slide-metric {
              padding: 10px !important;
            }

            .home-trust-grid {
              justify-content: stretch !important;
            }

            .home-slide-queue-item {
              align-items: flex-start !important;
              flex-direction: column !important;
              gap: 6px !important;
            }

            .home-slide-queue-heading {
              margin-bottom: 0.8rem !important;
            }

            .home-slide-metric div:first-child {
              font-size: 10px !important;
              margin-bottom: 4px !important;
            }
          }

          @media (max-width: 460px) {
            .home-banner-title {
              font-size: 1.28rem !important;
            }

            .home-banner-visual {
              min-height: 190px !important;
            }

            .home-banner-floating-card {
              padding: 10px !important;
            }

            .home-banner-floating-card-copy {
              font-size: 10.5px !important;
            }

            .homepage-whatsapp-banner img {
              min-height: 138px !important;
              max-height: 165px !important;
            }

            .homepage-whatsapp-banner-frame {
              min-height: 138px !important;
              aspect-ratio: 16 / 6.3 !important;
              border-radius: 14px !important;
            }

            .home-slide-primary {
              padding: 0.9rem !important;
            }

            .home-slide-queue {
              padding: 0.85rem !important;
            }

            .home-transaction-shell {
              padding: 0.75rem !important;
            }

            .home-slide-primary-title {
              font-size: 0.98rem !important;
            }

            .home-slide-primary-subtitle {
              font-size: 11px !important;
            }

            .home-slide-status-pill {
              font-size: 10px !important;
            }

            .home-slide-metric {
              padding: 9px !important;
            }
          }
        `}</style>
      </PageContainer>
    </PublicLayout>
  );
}

function SlideMetric({ label, value, mono = false, accent = false }) {
  return (
    <div
      className="home-slide-metric"
      style={{
        padding: '14px',
        borderRadius: '14px',
        background: 'rgba(255, 255, 255, 0.7)',
        border: '1px solid var(--clr-border)',
      }}
    >
      <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
        {label}
      </div>
      <div
        style={{
          fontSize: '14px',
          fontWeight: 700,
          color: accent ? 'var(--clr-accent)' : 'var(--clr-text)',
          fontFamily: mono ? 'var(--font-mono)' : 'var(--font-body)',
          wordBreak: 'break-word',
        }}
      >
        {value}
      </div>
    </div>
  );
}
